/* Busca de paciente compacta (combobox) para a barra superior do Plano Alimentar.
   Substitui o <select> antigo; chama onChange(id) igual ao select fazia. */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Ico } from '../ui/Ico';

const norm = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const initials = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return ((parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

export const ageFrom = (birth) => {
  if (!birth) return null;
  const b = new Date(String(birth).slice(0, 10) + 'T12:00:00');
  if (isNaN(b)) return null;
  const t = new Date();
  let a = t.getFullYear() - b.getFullYear();
  if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a--;
  return a >= 0 && a < 130 ? a : null;
};

export default function PatientPicker({ patients, value, onChange }) {
  const selected = useMemo(
    () => patients.find(p => String(p.id) === String(value)) || null,
    [patients, value]
  );
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const [hl, setHl]       = useState(0);
  const inputRef = useRef(null);
  const listRef  = useRef(null);

  const results = useMemo(() => {
    const q = norm(query);
    return (q ? patients.filter(p => norm(p.name).includes(q)) : patients).slice(0, 60);
  }, [patients, query]);

  /* Atalho "/" foca a busca de qualquer lugar (fora de campos de texto). */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && t.closest && t.closest('input, textarea, select, [contenteditable="true"]')) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* Mantém a opção destacada visível (sem scrollIntoView). */
  useEffect(() => {
    const list = listRef.current;
    const el = list?.children[hl];
    if (!el) return;
    if (el.offsetTop < list.scrollTop) list.scrollTop = el.offsetTop;
    else if (el.offsetTop + el.offsetHeight > list.scrollTop + list.clientHeight)
      list.scrollTop = el.offsetTop + el.offsetHeight - list.clientHeight;
  }, [hl, open]);

  const pick = (p) => {
    onChange(String(p.id));
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHl(h => Math.min(h + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHl(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { if (open && results[hl]) { e.preventDefault(); pick(results[hl]); } }
    else if (e.key === 'Escape') { setOpen(false); setQuery(''); inputRef.current?.blur(); }
  };

  return (
    <div className="pa-picker">
      <label className="pa-picker-box">
        {selected && !open
          ? <span className="pa-avatar" aria-hidden="true">{initials(selected.name)}</span>
          : <Ico e="🔍" size={16} />}
        <input
          ref={inputRef}
          className="pa-picker-input"
          role="combobox"
          aria-label="Buscar paciente"
          aria-expanded={open}
          aria-controls="pa-picker-list"
          aria-autocomplete="list"
          aria-activedescendant={open && results[hl] ? `pa-opt-${results[hl].id}` : undefined}
          placeholder={selected ? selected.name : 'Buscar paciente…'}
          value={open ? query : (selected?.name || '')}
          onChange={e => { setQuery(e.target.value); setHl(0); setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(''); setHl(0); }}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
          autoComplete="off"
        />
        {selected && !open ? (
          <button type="button" className="pa-picker-clear" title="Limpar paciente" aria-label="Limpar paciente"
            onMouseDown={e => e.preventDefault()} onClick={() => onChange('')}>
            <Ico e="✕" size={14} />
          </button>
        ) : <span className="pa-kbd" aria-hidden="true">/</span>}
      </label>

      {open && (
        <ul className="pa-picker-menu" id="pa-picker-list" role="listbox" ref={listRef}>
          {results.length === 0 ? (
            <li className="pa-picker-empty">
              {patients.length === 0 ? 'Carregando pacientes…' : 'Nenhum paciente encontrado'}
            </li>
          ) : results.map((p, i) => {
            const age = ageFrom(p.birth_date);
            return (
              <li key={p.id} id={`pa-opt-${p.id}`} role="option" aria-selected={i === hl}
                className="pa-picker-opt"
                onMouseEnter={() => setHl(i)}
                onMouseDown={e => { e.preventDefault(); pick(p); }}>
                <span className="pa-avatar">{initials(p.name)}</span>
                {p.name}
                {age !== null && <small>{age} anos</small>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
